import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { debugLogger } from "@/utils/debugLogger";

interface ProfileData {
  bio?: string | null;
  bio_topics?: string[] | null;
  bio_score?: number | null;
  occupation?: string | null;
  education?: string | null;
  height?: string | null;
  body_type?: string | null;
  hair_color?: string | null;
  eye_color?: string | null;
  smoker?: string | null;
  drinker?: string | null;
  piercings?: string | null;
  tattoos?: string | null;
  children?: string | null;
  relationship_type?: string | null;
  travel_availability?: string | null;
  marital_status?: string | null;
  sigilo_presenca?: string | null;
  profile_photos?: string[] | null;
}

interface StepInfo {
  id: string;
  title: string;
  fields: (keyof ProfileData)[];
}

interface CompleteProfileContextType {
  currentStep: number;
  totalSteps: number;
  requiredSteps: StepInfo[];
  data: ProfileData;
  updateData: (newData: Partial<ProfileData>) => void;
  autoSaveField: (field: string, value: any) => Promise<void>;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  saveCurrentStep: () => Promise<boolean>;
  loading: boolean;
  saving: boolean;
}

// New step definitions based on user request
const STEP_DEFINITIONS: StepInfo[] = [
  { id: 'bio', title: 'Bio', fields: ['bio'] },
  { id: 'career', title: 'Profissão e Formação', fields: ['occupation', 'education'] },
  { id: 'appearance', title: 'Aparência Física', fields: ['height', 'body_type', 'hair_color', 'eye_color'] },
  { id: 'lifestyle', title: 'Estilo de Vida', fields: ['smoker', 'drinker', 'piercings', 'tattoos', 'children'] },
  { id: 'relationships', title: 'Relacionamentos', fields: ['relationship_type', 'travel_availability', 'marital_status', 'sigilo_presenca'] },
];

const MINIMUM_PHOTOS = 3;
const BIO_MIN_LENGTH = 30;

// Helper function to check if a step is complete
const isStepComplete = (step: StepInfo, profile: any): boolean => {
  if (step.id === 'bio') {
    // Bio precisa ter texto E ter sido validada por IA (bio_score)
    return Boolean(
      profile?.bio && 
      profile.bio.length >= BIO_MIN_LENGTH && 
      profile?.bio_score !== null && 
      profile?.bio_score !== undefined
    );
  }
  if (step.id === 'photos') {
    return (profile?.profile_photos?.length || 0) >= MINIMUM_PHOTOS;
  }
  // For other steps, check if ALL fields are filled
  return step.fields.every(field => {
    const value = profile?.[field];
    return value !== null && value !== undefined && value !== '';
  });
};

// Calculate required steps based on profile data - ONLY CALLED ONCE
// IMPORTANT: Photos step is ALWAYS last (before completion) per user requirement
const calculateRequiredSteps = (profile: any): StepInfo[] => {
  const otherSteps: StepInfo[] = [];
  
  debugLogger.debug('CompleteProfile', 'Calculating required steps', { profile });
  
  // First, collect all non-photo incomplete steps
  for (const step of STEP_DEFINITIONS) {
    if (!isStepComplete(step, profile)) {
      otherSteps.push(step);
      debugLogger.debug('CompleteProfile', `Step "${step.id}" is incomplete`, { 
        fields: step.fields,
        values: step.fields.map(f => ({ field: f, value: profile?.[f] }))
      });
    }
  }
  
  // Photos step is ALWAYS added last (before completion) if needed
  const photoCount = profile?.profile_photos?.length || 0;
  const needsPhotos = photoCount < MINIMUM_PHOTOS;
  
  // Build final steps array: other steps first, then photos, then completion
  const steps: StepInfo[] = [...otherSteps];
  
  if (needsPhotos) {
    steps.push({ id: 'photos', title: 'Fotos', fields: [] });
    debugLogger.debug('CompleteProfile', 'Photos step added as last before completion', { photoCount, required: MINIMUM_PHOTOS });
  }
  
  // Always add completion step at the very end
  steps.push({ id: 'completion', title: 'Finalização', fields: [] });
  
  debugLogger.debug('CompleteProfile', 'Final required steps', { 
    stepIds: steps.map(s => s.id),
    totalSteps: steps.length 
  });
  
  return steps;
};

const CompleteProfileContext = createContext<CompleteProfileContextType | undefined>(undefined);

export const CompleteProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, profileData: authProfile, refreshProfileData } = useAuth();
  const navigate = useNavigate();
  
  const [data, setData] = useState<ProfileData>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // CRITICAL: Calculate required steps ONLY ONCE on initial load
  const [requiredSteps, setRequiredSteps] = useState<StepInfo[]>([]);
  const stepsCalculatedRef = useRef(false);

  const totalSteps = requiredSteps.length;

  // Load profile data and calculate steps ONCE
  useEffect(() => {
    if (authProfile && !stepsCalculatedRef.current) {
      debugLogger.debug('CompleteProfile', 'Initial load - calculating steps once', { 
        profileId: authProfile.id,
        bio: authProfile.bio?.substring(0, 50) 
      });
      
      // Set initial data
      setData({
        bio: authProfile.bio,
        occupation: authProfile.occupation,
        education: authProfile.education,
        height: authProfile.height,
        body_type: authProfile.body_type,
        hair_color: authProfile.hair_color,
        eye_color: authProfile.eye_color,
        smoker: authProfile.smoker,
        drinker: authProfile.drinker,
        piercings: authProfile.piercings,
        tattoos: authProfile.tattoos,
        children: authProfile.children,
        relationship_type: authProfile.relationship_type,
        travel_availability: authProfile.travel_availability,
        marital_status: authProfile.marital_status,
        sigilo_presenca: authProfile.sigilo_presenca,
        profile_photos: authProfile.profile_photos,
      });
      
      // Calculate steps ONCE based on initial profile data
      const steps = calculateRequiredSteps(authProfile);
      setRequiredSteps(steps);
      stepsCalculatedRef.current = true;
      
      setLoading(false);
    }
  }, [authProfile]);

  const updateData = useCallback((newData: Partial<ProfileData>) => {
    setData(prev => ({ ...prev, ...newData }));
  }, []);

  // Auto-save individual field to database immediately
  const autoSaveField = useCallback(async (field: string, value: any) => {
    if (!user?.id) return;
    
    try {
      debugLogger.debug('CompleteProfile', 'Auto-saving field', { field, value });
      
      const { error } = await supabase
        .from('profiles')
        .update({ [field]: value })
        .eq('id', user.id);
        
      if (error) {
        debugLogger.warn('CompleteProfile', 'Auto-save failed', { field, error });
        // Silent fail - data will be saved in nextStep() as fallback
      } else {
        debugLogger.debug('CompleteProfile', 'Field auto-saved', { field });
      }
    } catch (error) {
      debugLogger.warn('CompleteProfile', 'Auto-save error', { field, error });
      // Silent fail - don't interrupt UX
    }
  }, [user?.id]);

  const saveCurrentStep = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      debugLogger.warn('CompleteProfile', 'No user ID, cannot save');
      return false;
    }
    
    setSaving(true);
    try {
      const currentStepInfo = requiredSteps[currentStep - 1];
      debugLogger.debug('CompleteProfile', 'Attempting to save step', { 
        stepIndex: currentStep - 1,
        stepInfo: currentStepInfo,
        userId: user.id 
      });

      if (!currentStepInfo || currentStepInfo.id === 'completion') {
        debugLogger.debug('CompleteProfile', 'Skipping save - completion step');
        return true;
      }

      // Handle photos step - save photo paths to database
      if (currentStepInfo.id === 'photos') {
        const photos = data.profile_photos || [];
        const updatePayload: Record<string, any> = {
          profile_photos: photos,
          main_photo_url: photos.length > 0 ? photos[0] : null,
        };
        
        const { error } = await supabase
          .from('profiles')
          .update(updatePayload)
          .eq('id', user.id);
          
        if (error) {
          debugLogger.error('CompleteProfile', 'Failed to save photos:', error);
          throw error;
        }
        
        debugLogger.debug('CompleteProfile', 'Photos saved to database:', { count: photos.length });
        return true;
      }

      // Get only the fields for current step that have values
      const updatePayload: Record<string, any> = {};
      
      // Special handling for bio step - include bio_topics and bio_score
      if (currentStepInfo.id === 'bio') {
        if (data.bio) {
          updatePayload.bio = data.bio;
        }
        if (data.bio_topics && data.bio_topics.length > 0) {
          updatePayload.bio_topics = data.bio_topics;
        }
        if (data.bio_score !== undefined && data.bio_score !== null) {
          updatePayload.bio_score = data.bio_score;
        }
        updatePayload.bio_last_analyzed = new Date().toISOString();
      } else {
        // For other steps, iterate through fields
        for (const field of currentStepInfo.fields) {
          const value = data[field];
          if (value !== undefined && value !== null && value !== '') {
            updatePayload[field] = value;
          }
        }
      }

      debugLogger.debug('CompleteProfile', 'Update payload', { 
        step: currentStepInfo.id, 
        payload: updatePayload,
        fieldCount: Object.keys(updatePayload).length
      });

      if (Object.keys(updatePayload).length === 0) {
        debugLogger.warn('CompleteProfile', 'No fields to update');
        return true;
      }

      const { error, data: resultData } = await supabase
        .from('profiles')
        .update(updatePayload)
        .eq('id', user.id)
        .select();

      if (error) {
        debugLogger.error('CompleteProfile', 'Supabase update error', { error, payload: updatePayload });
        throw error;
      }

      debugLogger.debug('CompleteProfile', 'Step saved successfully', { 
        step: currentStepInfo.id, 
        fields: Object.keys(updatePayload),
        result: resultData 
      });
      return true;
    } catch (error) {
      debugLogger.error('CompleteProfile', 'Error saving step:', error);
      toast.error('Erro ao salvar. Tente novamente.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [user?.id, data, currentStep, requiredSteps]);

  const nextStep = useCallback(async () => {
    const success = await saveCurrentStep();
    if (!success) return;

    // Guard: ensure currentStep doesn't exceed requiredSteps.length
    if (currentStep < requiredSteps.length) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Profile complete - email is sent via database trigger (handle_onboarding_completion)
      // Just refresh profile data and redirect
      debugLogger.debug('CompleteProfile', 'Profile complete, email will be sent via backend trigger');
      await refreshProfileData();
      toast.success('Perfil completo! 🎉');
      navigate('/main/feed');
    }
  }, [currentStep, requiredSteps.length, saveCurrentStep, refreshProfileData, navigate]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalSteps]);

  const contextValue = useMemo(() => ({
    currentStep,
    totalSteps,
    requiredSteps,
    data,
    updateData,
    autoSaveField,
    nextStep,
    prevStep,
    goToStep,
    saveCurrentStep,
    loading,
    saving,
  }), [
    currentStep,
    totalSteps,
    requiredSteps,
    data,
    updateData,
    autoSaveField,
    nextStep,
    prevStep,
    goToStep,
    saveCurrentStep,
    loading,
    saving,
  ]);

  return (
    <CompleteProfileContext.Provider value={contextValue}>
      {children}
    </CompleteProfileContext.Provider>
  );
};

export const useCompleteProfile = () => {
  const context = useContext(CompleteProfileContext);
  if (!context) {
    throw new Error("useCompleteProfile must be used within CompleteProfileProvider");
  }
  return context;
};
